import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Loader2, Network, Store, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { OnboardingLinkPanel } from '@/pages/admin/customer-detail/OnboardingLinkPanel';
import {
  useAttachGroupUsers,
  useCustomerGroup,
  useEnableGroupStores,
  useSetCustomerGroup,
  useSetGroupBillingMode,
} from '@/hooks/admin/useCustomerGroup';

/**
 * Chain & franchise card (admin customer Overview) — shown when this
 * account's QBO customer has sub-customers, or when it's already marked as
 * a group master.
 *
 * Model: a brand (Starbird, The Melt) can have BOTH a corporate master
 * ("Chain HQ") and franchisee owner companies ("Franchise owner", e.g.
 * KEYSTONE FOODS) — each QBO parent is its own billing group. Stores enable
 * as their OWN portal accounts (own QBO account, own invoices); this card
 * marks the master, shows the QBO-derived roster, and copies the master's
 * users onto every enabled store so one login sees them all.
 */
export function ChainGroupCard({ customerId, customerName }: { customerId: string; customerName: string }) {
  const { data, isLoading, refetch } = useCustomerGroup(customerId);
  const setGroup = useSetCustomerGroup(customerId);
  const setBilling = useSetGroupBillingMode(customerId);
  const attach = useAttachGroupUsers(customerId);
  const enableStores = useEnableGroupStores(customerId);

  const [brand, setBrand] = useState<string | null>(null); // null = not edited yet
  const [notice, setNotice] = useState<string | null>(null);
  const [enablingAll, setEnablingAll] = useState(false);
  const cancelEnableAll = useRef(false);

  if (isLoading || !data) return null;

  const kind = data.group_kind;
  const brandValue = brand ?? data.group_brand ?? '';
  const enabledMembers = data.members.filter((m) => m.enabled);
  const notEnabledCount = data.members.length - enabledMembers.length;

  // "Enable all stores" — loops the time-budgeted sweep until remaining=0.
  const runEnableAll = async () => {
    setNotice(null);
    setEnablingAll(true);
    cancelEnableAll.current = false;
    const enabledNames: string[] = [];
    const skippedNames: string[] = [];
    const failedNames: string[] = [];
    try {
      // 40-call ceiling = a runaway-loop backstop, far above any real chain.
      for (let i = 0; i < 40; i += 1) {
        if (cancelEnableAll.current) break;
        const res = await enableStores.mutateAsync();
        enabledNames.push(...res.stores_enabled.map((s) => s.name));
        skippedNames.push(...res.skipped.map((s) => s.name));
        failedNames.push(...res.failures.map((f) => `${f.name} (${f.error})`));
        setNotice(`Enabling stores… ${enabledNames.length} done${res.remaining > 0 ? `, ${res.remaining} to go` : ''}`);
        if (res.remaining <= 0) break;
      }
      const parts = [
        `${enabledNames.length} store${enabledNames.length === 1 ? '' : 's'} enabled` +
          (enabledNames.length > 0 ? ` (${enabledNames.join(', ')})` : ''),
      ];
      if (skippedNames.length > 0) parts.push(`skipped internal buckets: ${skippedNames.join(', ')}`);
      if (failedNames.length > 0) parts.push(`FAILED: ${failedNames.join('; ')}`);
      setNotice(`Done — ${parts.join(' · ')}. Each store inherited this account's accounting & payment settings.`);
      void refetch();
    } catch (e) {
      setNotice(`Enable-all failed: ${e instanceof Error ? e.message : 'error'}`);
    } finally {
      setEnablingAll(false);
    }
  };

  const pick = (next: 'chain' | 'franchise' | null) => {
    setNotice(null);
    setGroup.mutate(
      { group_kind: next, group_brand: next ? brandValue || null : null },
      {
        onSuccess: () =>
          setNotice(
            next
              ? `Marked as ${next === 'chain' ? 'Chain HQ' : 'Franchise owner'}${brandValue ? ` · ${brandValue}` : ''}.`
              : 'Group marking removed.',
          ),
      },
    );
  };

  // This account is a MEMBER STORE of a group. Say whose, and link there.
  // It must not get the marking buttons: marking a store as a chain HQ is how
  // a chain ends up nested inside itself, and the group model is one level
  // deep. (Before this branch existed the compact card below claimed "Not
  // part of a group" on a store that plainly was one — the member state is
  // returned by the endpoint and was simply never rendered.)
  if (!data.is_qbo_parent && !kind && data.member_of) {
    const m = data.member_of;
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-display text-sm font-semibold text-brix-ink inline-flex items-center gap-2">
            <Network size={15} className="text-brix-primary" />
            Chain &amp; franchise
          </h2>
          <StatusBadge tone="info" size="sm">
            {m.group_kind === 'chain' ? 'Chain store' : 'Franchise location'}
            {m.group_brand ? ` \u00b7 ${m.group_brand}` : ''}
          </StatusBadge>
        </div>
        <p className="text-[13px] text-brix-muted">
          Part of{' '}
          <Link
            to={`/admin/customers/${m.master_customer_id}/overview`}
            className="font-semibold text-brix-primary hover:underline"
          >
            {m.master_name}
          </Link>
          {m.billing_mode === 'master' ? (
            <>
              {' '}— that account pays, and its accounting emails, routing, terms and payment
              methods flow down here
              {m.overrides.length > 0
                ? ` (${m.overrides.length} field${m.overrides.length === 1 ? '' : 's'} overridden on this store)`
                : ''}
              .
            </>
          ) : (
            <> — each store in the group bills and pays on its own account.</>
          )}{' '}
          This store bills its own QuickBooks account either way.
        </p>
      </div>
    );
  }

  // An account with no QBO sub-customers can still BE a franchise owner or a
  // chain HQ. A group being stood up has no stores under it in QuickBooks yet,
  // and that is exactly when someone needs to mark it — the /admin/links
  // console only lists accounts that are already marked or already parents, so
  // hiding this control until QuickBooks catches up made those accounts
  // impossible to mark anywhere. Rendered compact so it stays one line on the
  // ~150 customers that will never be a group.
  if (!data.is_qbo_parent && !kind) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-3 space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-display text-sm font-semibold text-brix-ink inline-flex items-center gap-2">
            <Network size={15} className="text-brix-muted" />
            Chain &amp; franchise
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={brandValue}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="Brand (optional)"
              className="h-8 w-36 rounded-lg border border-brix-border bg-brix-surface px-2.5 text-[13px] text-brix-ink placeholder:text-brix-muted/60"
            />
            <Button size="sm" variant="secondary" disabled={setGroup.isPending} onClick={() => pick('franchise')}>
              <Store size={14} className="mr-1.5" />
              Mark as franchise owner
            </Button>
            <Button size="sm" variant="secondary" disabled={setGroup.isPending} onClick={() => pick('chain')}>
              <Building2 size={14} className="mr-1.5" />
              Mark as chain HQ
            </Button>
          </div>
        </div>
        <p className="text-[13px] text-brix-muted">
          Not part of a group. QuickBooks shows no sub-customers under this account, so its store
          roster will be empty until stores are created under it there — marking it now is what puts
          it on the franchise / chain list and lets you mint a new-store signup link.
        </p>
        {notice && <p className="text-[13px] text-brix-primary">{notice}</p>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-display text-sm font-semibold text-brix-ink inline-flex items-center gap-2">
          <Network size={15} className="text-brix-primary" />
          Chain &amp; franchise
        </h2>
        {kind && (
          <StatusBadge tone="info" size="sm">
            {kind === 'chain' ? 'Chain HQ' : 'Franchise owner'}
            {data.group_brand ? ` · ${data.group_brand}` : ''}
          </StatusBadge>
        )}
      </div>

      <p className="text-[13px] text-brix-muted">
        {data.members.length === 0 ? (
          <>
            QuickBooks shows no sub-customers under this account yet — stores will appear here once
            they are created under it there.
          </>
        ) : (
          <>
            This account has {data.members.length} sub-customer{data.members.length === 1 ? '' : 's'} in
            QuickBooks.
          </>
        )}{' '}
        Each store bills its <b>own</b> QBO account (enable it as its own customer); marking this
        account as the group master lets its users see every store from one login.
      </p>

      {/* Kind + brand */}
      <div className="flex items-center gap-2 flex-wrap">
        {(
          [
            { v: null, label: 'Not a group' },
            { v: 'chain', label: 'Chain HQ' },
            { v: 'franchise', label: 'Franchise owner' },
          ] as const
        ).map(({ v, label }) => (
          <button
            key={label}
            type="button"
            disabled={setGroup.isPending}
            onClick={() => pick(v)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-[13px] font-semibold border cursor-pointer transition-colors',
              kind === v
                ? 'bg-brix-primary-tint text-brix-primary border-brix-primary/40'
                : 'text-brix-muted border-brix-border hover:text-brix-ink hover:bg-brix-primary-tint/40',
            )}
          >
            {label}
          </button>
        ))}
        <input
          value={brandValue}
          onChange={(e) => setBrand(e.target.value)}
          onBlur={() => {
            if (kind && (brand ?? data.group_brand ?? '') !== (data.group_brand ?? '')) pick(kind);
          }}
          placeholder="Brand (e.g. Starbird)"
          className="h-8 w-44 rounded-lg border border-brix-border bg-brix-surface px-2.5 text-[13px] text-brix-ink placeholder:text-brix-muted/60"
        />
      </div>

      {/* Billing mode — Master billing vs independent per store */}
      {kind && (
        <div className="rounded-lg border border-brix-border bg-brix-surface-alt/30 px-3.5 py-3 space-y-2">
          <p className="text-[13px] font-semibold text-brix-ink">Billing</p>
          <div className="flex items-center gap-2 flex-wrap">
            {(
              [
                {
                  v: 'master' as const,
                  label: 'Master billing',
                  hint: `All payments come from ${customerName} — one card, one payment for every store. Stores also inherit its accounting emails, routing, terms & payment methods (per-store overrides allowed)`,
                },
                {
                  v: 'independent' as const,
                  label: 'Independent',
                  hint: 'Each store pays its own invoices and manages its own accounting & payment settings',
                },
              ]
            ).map(({ v, label, hint }) => {
              const selected = (data.group_billing_mode ?? 'independent') === v;
              return (
                <button
                  key={v}
                  type="button"
                  disabled={setBilling.isPending}
                  title={hint}
                  onClick={() => {
                    setNotice(null);
                    setBilling.mutate(v, {
                      onSuccess: (r) =>
                        setNotice(
                          v === 'master'
                            ? `Master billing ON — accounting & payment settings pushed to ${r.stores_synced} store${r.stores_synced === 1 ? '' : 's'}. Master edits and newly enabled stores stay in sync; per-store overrides are respected.`
                            : 'Independent billing — each store keeps its own settings from here on.',
                        ),
                      onError: (e) =>
                        setNotice(`Billing mode failed: ${e instanceof Error ? e.message : 'error'}`),
                    });
                  }}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-[13px] font-semibold border cursor-pointer transition-colors',
                    selected
                      ? 'bg-brix-primary-tint text-brix-primary border-brix-primary/40'
                      : 'text-brix-muted border-brix-border hover:text-brix-ink hover:bg-brix-primary-tint/40',
                  )}
                >
                  {setBilling.isPending && !selected ? <Loader2 size={12} className="inline animate-spin mr-1" /> : null}
                  {label}
                </button>
              );
            })}
          </div>
          <p className="text-[12px] text-brix-muted">
            <span className="font-semibold text-brix-ink">
              Master billing means all payments come from this account.
            </span>{' '}
            Its accounts-payable people see every store's invoices in one list and settle them
            with this account's main payer — each store still gets its own QuickBooks payment
            and receipt, so the books stay per store. Stores also inherit this account's
            accounting emails (Primary / Secondary / Optional / Accounting), invoice / statement
            / reminder routing, payment terms, and per-category payment methods — now, on future
            edits here, and when new stores are enabled. A setting edited directly on a store
            becomes that store's override and stops inheriting (each store shows its overrides
            with a "Reset to master" option). The bill-to address printed on documents comes from
            QuickBooks.
          </p>
        </div>
      )}

      {/* The chain's public new-store link (/apply/:token) */}
      {kind && (
        <OnboardingLinkPanel
          customerId={customerId}
          chainLabel={data.group_brand ?? customerName}
        />
      )}

      {/* Members roster (live from QuickBooks parent links) */}
      <div className="rounded-lg border border-brix-border overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-brix-surface-alt/50 text-left text-brix-muted">
              <th className="px-3 py-2 font-semibold">Store (QBO sub-customer)</th>
              <th className="px-3 py-2 font-semibold w-24">QBO #</th>
              <th className="px-3 py-2 font-semibold w-40">Portal account</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((m) => (
              <tr key={m.qbo_customer_id} className="border-t border-brix-border">
                <td className="px-3 py-2 text-brix-ink">
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 size={13} className="text-brix-muted" />
                    {m.name}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono text-brix-muted">{m.qbo_customer_id}</td>
                <td className="px-3 py-2">
                  {m.enabled && m.orders_customer_id ? (
                    <Link
                      to={`/admin/customers/${m.orders_customer_id}`}
                      className="text-brix-primary font-semibold hover:underline"
                    >
                      {m.orders_active ? 'Enabled →' : 'Closed →'}
                    </Link>
                  ) : (
                    <span className="text-brix-muted">Not enabled</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Enable every not-yet-enabled store in one sweep */}
      {kind && notEnabledCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            disabled={enablingAll}
            onClick={() => {
              if (
                window.confirm(
                  `Enable ${notEnabledCount} store${notEnabledCount === 1 ? '' : 's'} under ${customerName}? ` +
                    `Each becomes its own billing account${
                      (data.group_billing_mode ?? 'independent') === 'master'
                        ? ` inheriting ${customerName}'s accounting & payment settings (statements + paper mail locked off)`
                        : ''
                    }. Internal EQUIPMENT/RESQ buckets are skipped. No signup emails are sent.`,
                )
              ) {
                void runEnableAll();
              }
            }}
          >
            {enablingAll ? <Loader2 size={13} className="animate-spin" /> : <Store size={13} />}
            {enablingAll ? 'Enabling stores…' : `Enable all stores (${notEnabledCount})`}
          </Button>
          {enablingAll && (
            <button
              type="button"
              className="text-[12px] text-brix-muted underline cursor-pointer"
              onClick={() => {
                cancelEnableAll.current = true;
              }}
            >
              Stop after this batch
            </button>
          )}
        </div>
      )}

      {/* Attach users */}
      {kind && (
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            disabled={attach.isPending || data.master_user_count === 0 || enabledMembers.length === 0}
            onClick={() => {
              setNotice(null);
              attach.mutate(undefined, {
                onSuccess: (r) => {
                  const attached = r.results.reduce((s, x) => s + x.attached + x.reactivated, 0);
                  setNotice(
                    attached > 0
                      ? `Access granted: ${attached} membership${attached === 1 ? '' : 's'} added across ${r.stores} store${r.stores === 1 ? '' : 's'}.`
                      : `All set — this account's users already have access to every enabled store.`,
                  );
                },
                onError: (e) => setNotice(`Attach failed: ${e instanceof Error ? e.message : 'error'}`),
              });
            }}
            title={
              data.master_user_count === 0
                ? `Add a user to ${customerName} first (Users tab)`
                : `Copy this account's ${data.master_user_count} user${data.master_user_count === 1 ? '' : 's'} onto every enabled store`
            }
          >
            {attach.isPending ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
            Give this account's users access to all stores
          </Button>
          <span className="text-[12px] text-brix-muted">
            {data.master_user_count} user{data.master_user_count === 1 ? '' : 's'} here ·{' '}
            {enabledMembers.length} of {data.members.length} stores enabled. New stores enabled later
            get access automatically.
          </span>
        </div>
      )}

      {notice && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[13px] text-sky-900">
          {notice}
        </div>
      )}
    </div>
  );
}
